import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, MapPin, Clock, Users } from 'lucide-react';
import { refreshMicrosoftToken } from '../services/oauth';
import toast from 'react-hot-toast';
import { OutlookIcon } from './icons/BrandIcons';


const OutlookCalendar = () => {
  const [events, setEvents] = useState([]);
  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCalendar, setSelectedCalendar] = useState('primary');
  const [viewMode] = useState('calendar'); // Always calendar view
  const [dateRange, setDateRange] = useState('month'); // 'today', 'upcoming', 'lastweek', 'last30days', 'month', 'custom'
  const [hasMicrosoftAuth, setHasMicrosoftAuth] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showAttendeesModal, setShowAttendeesModal] = useState(false);
  const [selectedAttendees, setSelectedAttendees] = useState([]);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [declineMessage, setDeclineMessage] = useState('');
  const [respondingToEvent, setRespondingToEvent] = useState(null);
  const [loadingResponse, setLoadingResponse] = useState(false);
  const [loadingAccept, setLoadingAccept] = useState(false);
  const [loadingDecline, setLoadingDecline] = useState(false);

  useEffect(() => {
    checkMicrosoftAuth();
  }, []);

  useEffect(() => {
    if (hasMicrosoftAuth) {
      fetchCalendars();
      fetchEvents();
    }
  }, [selectedCalendar, dateRange, hasMicrosoftAuth]);

  const checkMicrosoftAuth = async () => {
    try {
      // Check Microsoft Calendar connection status
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/calendar/status', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success && response.data.data.connected) {
        setHasMicrosoftAuth(true);
        // If connected, fetch calendars and events
        fetchCalendars();
        fetchEvents();
      } else {
        setHasMicrosoftAuth(false);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        // User doesn't have Microsoft OAuth configured
        setHasMicrosoftAuth(false);
      } else {
        console.error('Error checking Microsoft auth:', error);
        setHasMicrosoftAuth(false);
      }
    } finally {
      setCheckingAuth(false);
    }
  };

  const handleMicrosoftLogin = () => {
    // Redirect to Microsoft OAuth
    const clientId = process.env.REACT_APP_MICROSOFT_CLIENT_ID || 'your_client_id_here';
    const redirectUri = encodeURIComponent(`${window.location.origin}/auth/microsoft/callback`);
    const scope = encodeURIComponent('openid email profile Calendars.Read Calendars.ReadWrite User.Read offline_access');
    
    const authUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${clientId}&response_type=code&redirect_uri=${redirectUri}&scope=${scope}&response_mode=query&prompt=consent`;
    
    window.location.href = authUrl;
  };

  const handleRefreshAuth = () => {
    setCheckingAuth(true);
    checkMicrosoftAuth();
  };

  const fetchCalendars = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/calendar/list', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setCalendars(response.data.calendars);
    } catch (error) {
      if (error.response?.status === 401) {
        // Try to refresh the Microsoft token
        try {
          await refreshMicrosoftToken();
          // Retry the request
          const retryToken = localStorage.getItem('token');
          const retryResponse = await axios.get('/api/calendar/list', {
            headers: {
              'Authorization': `Bearer ${retryToken}`
            }
          });
          setCalendars(retryResponse.data.calendars);
        } catch (refreshError) {
          console.error('Failed to refresh Microsoft token:', refreshError);
          setHasMicrosoftAuth(false);
          toast.error('Microsoft Calendar connection expired. Please reconnect.');
        }
      } else {
        console.error('Error fetching calendars:', error);
        toast.error('Failed to load calendars');
      }
    }
  };

  const fetchEvents = async () => {
    setLoading(true);
    let endpoint = '/api/calendar/events';
    let params = { calendar_id: selectedCalendar };
    
    try {
      if (dateRange === 'today') {
        // Use the general events endpoint with today's date range
        const today = new Date();
        const startDate = new Date(today);
        startDate.setHours(0, 0, 0, 0);
        const endDate = new Date(today);
        endDate.setHours(23, 59, 59, 999);
        
        endpoint = '/api/calendar/events';
        params = { 
          calendar_id: selectedCalendar,
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString(),
          max_results: 50
        };
      } else if (dateRange === 'upcoming') {
        endpoint = '/api/calendar/upcoming';
        params = { days: 7 };
      } else if (dateRange === 'lastweek') {
        // Fetch events from last week (7 days ago to today)
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 7);
        
        endpoint = '/api/calendar/events';
        params = { 
          calendar_id: selectedCalendar,
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString(),
          max_results: 50
        };
      } else if (dateRange === 'last30days') {
        // Fetch events from 30 days ago to today
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 30);
        
        endpoint = '/api/calendar/events';
        params = { 
          calendar_id: selectedCalendar,
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString(),
          max_results: 100
        };
      } else if (dateRange === 'month') {
        // Fetch events for the entire current month
        const today = new Date();
        const startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        const endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        endDate.setHours(23, 59, 59, 999);
        
        endpoint = '/api/calendar/events';
        params = { 
          calendar_id: selectedCalendar,
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString(),
          max_results: 500
        };
      }

      const token = localStorage.getItem('token');
      const response = await axios.get(endpoint, { 
        params,
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setEvents(response.data.events);
    } catch (error) {
      if (error.response?.status === 401) {
        // Try to refresh the Microsoft token
        try {
          await refreshMicrosoftToken();
          // Retry the request
          const retryToken = localStorage.getItem('token');
          const retryResponse = await axios.get(endpoint, { 
            params,
            headers: {
              'Authorization': `Bearer ${retryToken}`
            }
          });
          setEvents(retryResponse.data.events);
        } catch (refreshError) {
          console.error('Failed to refresh Microsoft token:', refreshError);
          setHasMicrosoftAuth(false);
          toast.error('Microsoft Calendar connection expired. Please reconnect.');
        }
      } else {
        console.error('Error fetching events:', error);
        toast.error('Failed to load calendar events');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateTime) => {
    return new Date(dateTime).toLocaleString();
  };

  const formatTime = (dateTime) => {
    return new Date(dateTime).toLocaleTimeString();
  };

  const generateCalendarDays = (events, dateRange) => {
    const today = new Date();
    let startDate, endDate;
    
    if (dateRange === 'today') {
      startDate = new Date(today);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(today);
      endDate.setHours(23, 59, 59, 999);
    } else if (dateRange === 'upcoming') {
      // Show current week + next week
      startDate = new Date(today);
      startDate.setDate(today.getDate() - today.getDay()); // Start from Sunday of current week
      endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 13); // Show 2 weeks
    } else if (dateRange === 'lastweek') {
      // Show last week (7 days)
      endDate = new Date(today);
      startDate = new Date(today);
      startDate.setDate(today.getDate() - 7);
    } else if (dateRange === 'last30days') {
      // Show last 30 days
      endDate = new Date(today);
      startDate = new Date(today);
      startDate.setDate(today.getDate() - 30);
    } else if (dateRange === 'month') {
      // Show current month
      startDate = new Date(today.getFullYear(), today.getMonth(), 1);
      endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    } else {
      // Default to current month
      startDate = new Date(today.getFullYear(), today.getMonth(), 1);
      endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    }

    const days = [];
    let currentDate = new Date(startDate);
    
    // Add padding days to start from Sunday
    if (currentDate.getDay() !== 0) {
      const paddingStart = new Date(currentDate);
      paddingStart.setDate(currentDate.getDate() - currentDate.getDay());
      for (let i = 0; i < currentDate.getDay(); i++) {
        days.push({
          date: paddingStart.getDate() + i,
          isToday: false,
          isOtherMonth: true,
          isPadding: true,
          events: []
        });
      }
    }

    while (currentDate <= endDate) {
      const isToday = currentDate.toDateString() === today.toDateString();
      const isOtherMonth = currentDate.getMonth() !== today.getMonth();

      const eventsForDay = events.filter(event => {
        const eventDate = new Date(event.start_time);
        return eventDate.toDateString() === currentDate.toDateString();
      });

      days.push({
        date: currentDate.getDate(),
        isToday,
        isOtherMonth,
        isPadding: false,
        events: eventsForDay,
        fullDate: new Date(currentDate)
      });
      
      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Add padding days to end on Saturday
    while (days.length % 7 !== 0) {
      days.push({
        date: currentDate.getDate(),
        isToday: false,
        isOtherMonth: true,
        isPadding: true,
        events: []
      });
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return days;
  };

  const showEventDetails = (event) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  const closeEventModal = () => {
    setShowEventModal(false);
    setSelectedEvent(null);
  };

  const showAttendeesList = (attendees, eventTitle) => {
    setSelectedAttendees(attendees || []);
    setShowAttendeesModal(true);
  };

  const closeAttendeesModal = () => {
    setShowAttendeesModal(false);
    setSelectedAttendees([]);
  };

  const handleAcceptEvent = async (event) => {
    try {
      setLoadingAccept(true);
      const token = localStorage.getItem('token');
      await axios.post(`/api/calendar/events/${event.id}/respond`, {
        response: 'accepted',
        comment: ''
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success('Event accepted successfully!');
      // Refresh events to show updated status
      fetchEvents();
      closeEventModal();
    } catch (error) {
      console.error('Error accepting event:', error);
      toast.error('Failed to accept event');
    } finally {
      setLoadingAccept(false);
    }
  };

  const handleDeclineEvent = (event) => {
    setRespondingToEvent(event);
    setShowDeclineModal(true);
  };

  const confirmDeclineEvent = async () => {
    try {
      setLoadingDecline(true);
      const token = localStorage.getItem('token');
      await axios.post(`/api/calendar/events/${respondingToEvent.id}/respond`, {
        response: 'declined',
        comment: declineMessage
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success('Event declined successfully!');
      // Refresh events to show updated status
      fetchEvents();
      setShowDeclineModal(false);
      setDeclineMessage('');
      setRespondingToEvent(null);
      closeEventModal();
    } catch (error) {
      console.error('Error declining event:', error);
      toast.error('Failed to decline event');
    } finally {
      setLoadingDecline(false);
    }
  };

  const cancelDecline = () => {
    setShowDeclineModal(false);
    setDeclineMessage('');
    setRespondingToEvent(null);
  };

  // Function to check if current user has already accepted the event
  const hasUserAccepted = (event) => {
    if (!event.attendees) return false;
    
    // Get current user's email from localStorage or context
    const currentUserEmail = localStorage.getItem('userEmail') || 'abdul.wassay@xloopdigital.com'; // Fallback for testing
    
    for (const attendee of event.attendees) {
      const email = typeof attendee === 'string' ? attendee : attendee.email || attendee.address;
      const status = typeof attendee === 'object' ? attendee.status : null;
      
      if (email && email.toLowerCase() === currentUserEmail.toLowerCase() && status === 'accepted') {
        return true;
      }
    }
    
    return false;
  };

  return (
    <div className="w-full px-2 sm:px-4 lg:px-6 py-6 bg-gray-50 min-h-screen font-sans">
      {/* Compact Header Section */}
      <div className="mb-4">
        <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-sm border border-gray-200 dark:border-secondary-700 p-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-2">
                <div className="p-1.5 bg-[#0078D4]/10 rounded-md">
                  <OutlookIcon className="h-6 w-6" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-gray-900 dark:text-white">
                    Outlook Calendar
                  </h1>
                  <p className="text-xs text-gray-500 dark:text-secondary-400">
                    View and manage your calendar events
                  </p>
                </div>
              </div>
            </div>
            
            {/* Compact Quick Stats */}
            <div className="hidden lg:flex items-center space-x-4">
              <div className="text-center">
                <div className="flex items-center justify-center w-8 h-8 bg-blue-50 rounded-lg mb-1">
                  <span className="text-sm font-bold text-blue-600">{events.length}</span>
                </div>
                <div className="text-xs font-medium text-gray-600">Total</div>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center w-8 h-8 bg-green-50 rounded-lg mb-1">
                  <span className="text-sm font-bold text-green-600">
                    {events.filter(e => new Date(e.start_time) >= new Date()).length}
                  </span>
                </div>
                <div className="text-xs font-medium text-gray-600">Upcoming</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Microsoft Connection Required */}
      {checkingAuth ? (
        <div className="mb-4 bg-white dark:bg-secondary-800 rounded-lg shadow p-4 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Checking calendar connection...</p>
        </div>
      ) : !hasMicrosoftAuth ? (
        <div className="mb-4 bg-white dark:bg-secondary-800 rounded-lg shadow p-4 text-center">
          <div className="max-w-md mx-auto">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-[#0078D4]/10 mb-3">
              <OutlookIcon className="h-7 w-7" />
            </div>
            <h3 className="text-base font-medium text-gray-900 dark:text-white mb-2">
              Connect Your Outlook Calendar
            </h3>
            <p className="text-sm text-gray-600 dark:text-secondary-400 mb-4">
              To access your Outlook calendar, you need to connect your Microsoft account. 
              This will allow you to view your calendar events.
            </p>
            <button
              onClick={handleMicrosoftLogin}
              className="bg-[#0078D4] text-white px-4 py-2 rounded-lg hover:bg-[#106EBE] focus:outline-none focus:ring-2 focus:ring-[#0078D4] focus:ring-offset-2 inline-flex items-center mx-auto gap-2"
            >
              <OutlookIcon className="w-5 h-5" />
              Connect Microsoft Account
            </button>
            <div className="mt-3 flex items-center justify-center space-x-3">
              <button
                onClick={handleRefreshAuth}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                Already connected? Refresh
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-3">
              You'll be redirected to Microsoft to authorize access to your calendar.
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* Compact Controls */}
          <div className="mb-4">
            <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-sm border border-gray-200 dark:border-secondary-700 p-3">
              <div className="flex items-center">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Time Period
                  </label>
                  <select
                    value={dateRange}
                    onChange={(e) => setDateRange(e.target.value)}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm hover:border-gray-400 transition-colors"
                  >
                    <option value="today">Today</option>
                    <option value="upcoming">Next 7 Days</option>
                    <option value="lastweek">Last Week</option>
                    <option value="last30days">Last 30 Days</option>
                    <option value="month">Current Month</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Calendar Display */}
          <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-sm border border-gray-200 dark:border-secondary-700 overflow-hidden">
              {loading ? (
                <div className="p-4 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Loading calendar events...</p>
                </div>
              ) : events.length === 0 ? (
                <div className="p-4 text-center">
                  <Calendar className="mx-auto h-8 w-8 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No events</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    No calendar events found for the selected time period.
                  </p>
                </div>
              ) : (
                <div className="p-0 overflow-x-auto">
                  <div className="min-w-[360px]">
                  {/* Calendar Header - Modern Outlook Style */}
                  <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
                    {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map(day => (
                      <div key={day} className="text-center text-xs sm:text-sm font-semibold text-gray-700 py-2 border-r border-gray-200 last:border-r-0">
                        <div className="hidden lg:block">{day}</div>
                        <div className="hidden sm:block lg:hidden">{day.substring(0, 3)}</div>
                        <div className="sm:hidden">{day.substring(0, 1)}</div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Calendar Days - Outlook Style */}
                  <div className="grid grid-cols-7">
                    {generateCalendarDays(events, dateRange).map((day, index) => (
                      <div
                        key={index}
                        className={`min-h-14 sm:min-h-16 lg:min-h-20 border-r border-b border-gray-200 last:border-r-0 relative overflow-hidden ${
                          day.isToday 
                            ? 'bg-blue-50 ring-2 ring-blue-500 ring-inset' 
                            : day.isOtherMonth 
                              ? 'bg-gray-50' 
                              : 'bg-white hover:bg-gray-50'
                        } transition-colors duration-150`}
                      >
                        {/* Date Number */}
                        <div className="p-1">
                          <div className={`inline-flex items-center justify-center w-5 h-5 text-xs font-medium rounded-full ${
                            day.isToday 
                              ? 'bg-blue-600 text-white shadow-lg' 
                              : day.isOtherMonth 
                                ? 'text-gray-400' 
                                : 'text-gray-900 hover:bg-gray-100'
                          } transition-colors duration-150`}>
                            {day.date}
                          </div>
                        </div>
                        
                        {/* Events */}
                        <div className="px-1 pb-1 space-y-0.5">
                          {day.events.map((event, eventIndex) => (
                            <div
                              key={eventIndex}
                              className="group cursor-pointer"
                              onClick={() => showEventDetails(event)}
                            >
                              <div className={`text-xs px-1 py-0.5 rounded font-medium truncate transition-all duration-150 ${
                                event.importance === 'high' 
                                  ? 'bg-red-100 text-red-800 border-l-2 border-red-500 hover:bg-red-200' 
                                  : event.showAs === 'busy' 
                                    ? 'bg-blue-100 text-blue-800 border-l-2 border-blue-500 hover:bg-blue-200'
                                    : event.showAs === 'tentative'
                                      ? 'bg-yellow-100 text-yellow-800 border-l-2 border-yellow-500 hover:bg-yellow-200'
                                      : 'bg-gray-100 text-gray-800 border-l-2 border-gray-500 hover:bg-gray-200'
                              } group-hover:shadow-sm`}>
                                <div className="flex items-center justify-between">
                                  <div className="font-semibold truncate text-xs flex-1">{event.subject}</div>
                                  {event.user_response_status && event.user_response_status !== 'none' && (
                                    <div className="ml-1 flex-shrink-0">
                                      {event.user_response_status === 'accepted' && (
                                        <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                      )}
                                      {event.user_response_status === 'declined' && (
                                        <svg className="w-3 h-3 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                        </svg>
                                      )}
                                      {event.user_response_status === 'tentative' && (
                                        <svg className="w-3 h-3 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                        </svg>
                                      )}
                                    </div>
                                  )}
                                </div>
                                {!event.is_all_day && (
                                  <div className="text-xs opacity-75">
                                    {formatTime(event.start_time)}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        {/* Today indicator dot */}
                        {day.isToday && (
                          <div className="absolute top-1 right-1">
                            <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  </div>
                </div>
              )}
            </div>
        </>
      )}

      {/* Event Details Modal */}
      {showEventModal && selectedEvent && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={closeEventModal}
        >
          <div 
            className="bg-white dark:bg-secondary-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] max-h-[90dvh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-gray-900">
                  {selectedEvent.subject}
                </h3>
                <button
                  onClick={closeEventModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center">
                    <Clock className="mr-2 h-5 w-5 text-gray-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Time</p>
                      <p className="text-sm text-gray-600">
                        {selectedEvent.is_all_day 
                          ? 'All day' 
                          : `${formatDateTime(selectedEvent.start_time)} - ${formatTime(selectedEvent.end_time)}`
                        }
                      </p>
                    </div>
                  </div>

                  {selectedEvent.location && (
                    <div className="flex items-center">
                      <MapPin className="mr-2 h-5 w-5 text-gray-500" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Location</p>
                        <p className="text-sm text-gray-600">{selectedEvent.location}</p>
                      </div>
                    </div>
                  )}

                  {selectedEvent.attendees && selectedEvent.attendees.length > 0 && (
                    <div 
                      className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors duration-150"
                      onClick={() => showAttendeesList(selectedEvent.attendees, selectedEvent.subject)}
                    >
                      <Users className="mr-2 h-5 w-5 text-gray-500" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Attendees</p>
                        <p className="text-sm text-blue-600 hover:text-blue-700">
                          {selectedEvent.attendees.length} attendee(s) - Click to view
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {selectedEvent.description && (
                  <div>
                    <p className="text-sm font-medium text-gray-900 mb-2">Description</p>
                    <div 
                      className="text-sm text-gray-600 prose prose-sm max-w-none border border-gray-200 rounded-lg p-3 bg-gray-50 font-sans [&>*]:font-sans [&>*]:text-gray-600"
                      dangerouslySetInnerHTML={{ 
                        __html: selectedEvent.description.replace(/<style[^>]*>.*?<\/style>/gi, '').replace(/style="[^"]*"/gi, '')
                      }}
                    />
                  </div>
                )}

                <div className="text-xs text-gray-500">
                  Created: {formatDateTime(selectedEvent.created_at || selectedEvent.start_time)}
                </div>

                {/* Accept/Decline Buttons */}
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-gray-700">
                      Respond to this meeting:
                    </div>
                    {(selectedEvent.user_response_status && selectedEvent.user_response_status !== 'none' && selectedEvent.user_response_status !== 'notResponded') || hasUserAccepted(selectedEvent) ? (
                      <div className="flex items-center space-x-2">
                        <div className="flex items-center px-3 py-2 text-sm font-medium rounded-md bg-gray-100 text-gray-700">
                          {(selectedEvent.user_response_status === 'accepted' || selectedEvent.user_response_status === 'Accepted' || hasUserAccepted(selectedEvent)) && (
                            <>
                              <svg className="w-4 h-4 mr-2 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              Accepted
                            </>
                          )}
                          {(selectedEvent.user_response_status === 'declined' || selectedEvent.user_response_status === 'Declined') && (
                            <>
                              <svg className="w-4 h-4 mr-2 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                              </svg>
                              Declined
                            </>
                          )}
                          {(selectedEvent.user_response_status === 'tentative' || selectedEvent.user_response_status === 'Tentative') && (
                            <>
                              <svg className="w-4 h-4 mr-2 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                              </svg>
                              Tentative
                            </>
                          )}
                          {/* Show any other status values */}
                          {!['accepted', 'declined', 'tentative', 'Accepted', 'Declined', 'Tentative'].includes(selectedEvent.user_response_status) && !hasUserAccepted(selectedEvent) && (
                            <>
                              <svg className="w-4 h-4 mr-2 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                              </svg>
                              {selectedEvent.user_response_status}
                            </>
                          )}
                        </div>
                      </div>
                    ) : (
                      // Only show Accept/Decline buttons for future events
                      new Date(selectedEvent.end_time) > new Date() ? (
                        <div className="flex space-x-3">
                          <button
                            onClick={() => handleAcceptEvent(selectedEvent)}
                            disabled={loadingAccept || loadingDecline || selectedEvent.user_response_status === 'accepted' || selectedEvent.user_response_status === 'Accepted' || hasUserAccepted(selectedEvent)}
                            className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md transition-colors duration-150 ${
                              loadingAccept 
                                ? 'bg-green-400 text-white cursor-not-allowed' 
                                : (selectedEvent.user_response_status === 'accepted' || selectedEvent.user_response_status === 'Accepted' || hasUserAccepted(selectedEvent))
                                  ? 'bg-green-100 text-green-700 cursor-not-allowed'
                                  : 'bg-green-600 text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500'
                            }`}
                          >
                            {loadingAccept ? (
                              <>
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Accepting...
                              </>
                            ) : (selectedEvent.user_response_status === 'accepted' || selectedEvent.user_response_status === 'Accepted' || hasUserAccepted(selectedEvent)) ? (
                              <>
                                <svg className="w-4 h-4 mr-2 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                Accepted
                              </>
                            ) : (
                              <>
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                Accept
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => handleDeclineEvent(selectedEvent)}
                            disabled={loadingAccept || loadingDecline}
                            className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white transition-colors duration-150 ${
                              loadingDecline 
                                ? 'bg-red-400 cursor-not-allowed' 
                                : 'bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500'
                            }`}
                          >
                            {loadingDecline ? (
                              <>
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Declining...
                              </>
                            ) : (
                              <>
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                Decline
                              </>
                            )}
                          </button>
                        </div>
                    ) : (
                      <div className="flex items-center px-3 py-2 text-sm font-medium rounded-md bg-gray-100 text-gray-500">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Event has ended
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Attendees Modal */}
      {showAttendeesModal && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={closeAttendeesModal}
        >
          <div 
            className="bg-white dark:bg-secondary-800 rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Users className="mr-2 h-5 w-5 text-blue-600" />
                  Attendees ({selectedAttendees.length})
                </h3>
                <button
                  onClick={closeAttendeesModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors duration-150"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-3">
                {selectedAttendees.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No attendees found</p>
                ) : (
                  selectedAttendees.map((attendee, index) => {
                    // Handle different formats - could be just email string or object
                    const email = typeof attendee === 'string' ? attendee : attendee.email || attendee.address;
                    const name = typeof attendee === 'object' ? attendee.name : null;
                    const status = typeof attendee === 'object' ? attendee.status : null;
                    const type = typeof attendee === 'object' ? attendee.type : null;
                    
                    return (
                      <div key={index} className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-150">
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-blue-600 font-semibold text-sm">
                              {name ? name.charAt(0).toUpperCase() : email.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="ml-3 flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            {name && (
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {name}
                              </p>
                            )}
                            {type === 'optional' && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                Optional
                              </span>
                            )}
                            {type === 'required' && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                Required
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 truncate">
                            {email}
                          </p>
                          {status && status !== 'none' && (
                            <p className="text-xs text-gray-500 capitalize">
                              Status: <span className={`font-medium ${
                                status === 'accepted' ? 'text-green-600' :
                                status === 'declined' ? 'text-red-600' :
                                status === 'tentative' ? 'text-yellow-600' :
                                'text-gray-600'
                              }`}>
                                {status}
                              </span>
                            </p>
                          )}
                        </div>
                        <div className="flex-shrink-0">
                          <a
                            href={`mailto:${email}`}
                            className="text-blue-600 hover:text-blue-700 transition-colors duration-150"
                            title="Send email"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                          </a>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Decline Confirmation Modal */}
      {showDeclineModal && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={cancelDecline}
        >
          <div 
            className="bg-white dark:bg-secondary-800 rounded-lg shadow-xl max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <svg className="mr-2 h-5 w-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  Decline Meeting
                </h3>
                <button
                  onClick={cancelDecline}
                  className="text-gray-400 hover:text-gray-600 transition-colors duration-150"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-3">
                  Are you sure you want to decline "{respondingToEvent?.subject}"?
                </p>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Optional message to organizer:
                </label>
                <textarea
                  value={declineMessage}
                  onChange={(e) => setDeclineMessage(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  rows="3"
                  placeholder="Enter a reason for declining (optional)..."
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={cancelDecline}
                  disabled={loadingDecline}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeclineEvent}
                  disabled={loadingDecline}
                  className={`px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md transition-colors duration-150 ${
                    loadingDecline 
                      ? 'bg-red-400 cursor-not-allowed' 
                      : 'bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500'
                  }`}
                >
                  {loadingDecline ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Declining...
                    </>
                  ) : (
                    'Decline Meeting'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OutlookCalendar;
